import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { Button, PageContainer, PageIntroCard, PageSectionCard, toast } from '@kmgestao/ui';

import { FullPageSpinner } from '../../../components/FullPageSpinner.js';
import { useSession } from '../../../features/auth/index.js';
import { decodeJwtClaims } from '../../../features/auth/jwt-claims.js';
import { supabase } from '../../../lib/supabase.js';
import { ClinicShell } from '../ClinicShell.js';

import { PasswordSetForm } from './PasswordSetForm.js';

/**
 * `/welcome` — página de aterrissagem do magic link de invite/ativação.
 *
 * **PÚBLICA** (fora de RequireAuth) porque o magic link gerado por
 * `auth.admin.generateLink()` no edge function chega com tokens no
 * fragment (`#access_token=...&refresh_token=...`). Em PKCE flow, o
 * `supabase-js` não processa esse formato automaticamente — precisamos
 * fazer `setSession(...)` explícito ANTES do RequireAuth conseguir ver
 * a sessão. Por isso a página é pública e ela mesma estabelece a
 * sessão antes de aceitar o invite.
 *
 * ## Fluxo (PR-M2-00, 2026-04-25)
 *
 * Antes (PR-M1): /welcome marcava member 'active', refresh JWT, mostrava
 * boas-vindas + botão "Ir para o painel" → /dashboard. **Não pedia
 * senha**, então owner não conseguia logar de volta sem novo magic link.
 *
 * Agora:
 *
 *   1. **Fase `establishing-session`** — Detecta tokens no fragment
 *      (`#access_token=...`), faz `supabase.auth.setSession()` explícito,
 *      limpa fragment.
 *   2. **Fase `password-required`** — Se `user.user_metadata.password_set`
 *      não é `true`, mostra form `<PasswordSetForm>`. Owner define senha
 *      (8+ chars + força + confirmação). Salva via `auth.updateUser` com
 *      `data: { password_set: true }`.
 *   3. **Fase `accepting`** — Após senha salva (ou já existente), aceita
 *      memberships pendentes (UPDATE clinic_members.status='active'),
 *      transita lead → 'provisioned' + activity 'invite_accepted',
 *      refresh JWT.
 *   4. **Fase `routing`** — Decide destino baseado no claim
 *      `current_clinic_subscription_status` do JWT atualizado:
 *      - `incomplete` → `/clinic/payment-pending` (gate Modelo C híbrido)
 *      - `trialing` / `active` / `past_due` / null → `/dashboard`
 *      - `canceled` / `paused` → `/clinic/suspended`
 *
 * Caminhos alternativos:
 *   - Sem fragment + sem sessão → redireciona pra /login (link inválido/expirado)
 *   - Com sessão existente E `password_set: true` → pula form, vai direto pra accepting
 *   - Erro no setSession ou updateUser → toast + estado de erro com botão retry
 */

type Phase =
  | 'pending'
  | 'establishing-session'
  | 'password-required'
  | 'accepting'
  | 'routing'
  | 'error';

export function WelcomePage() {
  const { loading, session } = useSession();
  const navigate = useNavigate();
  const { t } = useT('clinics');
  const sessionEstablishedRef = useRef(false);
  const acceptedRef = useRef(false);
  const [phase, setPhase] = useState<Phase>('pending');

  /**
   * Fase 1: Estabelecer sessão a partir do fragment do magic link.
   * Roda apenas uma vez (guard `sessionEstablishedRef`). Decide se vai
   * pra `password-required` ou direto pra `accepting` (caso owner já
   * tenha `password_set: true`).
   */
  useEffect(() => {
    if (sessionEstablishedRef.current) return;
    if (loading) return;
    sessionEstablishedRef.current = true;

    void (async () => {
      setPhase('establishing-session');
      const hash = window.location.hash;
      let activeSession = session;

      if (hash && hash.includes('access_token')) {
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        if (accessToken && refreshToken) {
          const setRes = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (setRes.error) {
            toast.error(t('welcome.errors.acceptFailed'), {
              description: setRes.error.message,
            });
            setPhase('error');
            return;
          }
          activeSession = setRes.data.session;
          // Limpa fragment da URL (não queremos tokens em browser
          // history). Mantém pathname.
          window.history.replaceState({}, '', window.location.pathname);
        }
      }

      // Sem sessão (fragment ausente E sem sessão prévia) → link inválido
      // ou owner errou de URL.
      if (!activeSession) {
        navigate('/login', { replace: true });
        return;
      }

      // Detecta se é primeira ativação (sem `password_set` no metadata).
      // Owner criado por `provision-clinic` tem só `full_name` + `onboarded_via`
      // — `password_set` só vira `true` quando o `PasswordSetForm` salva
      // a senha. Em revisitas ao link de invite ou login normal, a flag
      // já existe e pulamos o form.
      const passwordSet = activeSession.user.user_metadata?.['password_set'] === true;
      if (!passwordSet) {
        setPhase('password-required');
        return;
      }

      // Já tem senha → segue direto pro aceite.
      void runAcceptance(activeSession.user.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /**
   * Fase 3: Aceitar membership + transitar lead. Idempotente — pode
   * rodar múltiplas vezes sem efeito acumulado.
   */
  async function runAcceptance(userId: string) {
    if (acceptedRef.current) return;
    acceptedRef.current = true;
    setPhase('accepting');

    // 1. Aceita memberships pendentes do user atual (idempotente).
    //    `.select('id, clinic_id')` retorna linhas afetadas — usamos
    //    pra correlacionar com leads/lead_activities.
    const updateRes = await supabase
      .from('clinic_members')
      .update({ status: 'active', accepted_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('status', 'invited')
      .select('id, clinic_id');

    if (updateRes.error) {
      toast.error(t('welcome.errors.acceptFailed'), {
        description: updateRes.error.message,
      });
      setPhase('error');
      return;
    }

    const acceptedRows = (updateRes.data ?? []) as { id: string; clinic_id: string }[];

    // 2. Pra cada clinic aceita, marca lead correspondente como
    //    'provisioned' (regra de negócio Alexandre 2026-04-25:
    //    lead só vira "cliente" após confirmação do aceite, não no
    //    provision-clinic). Pega lead via clinic_provisioning_events
    //    metadata (single source da relação clinic ↔ lead).
    for (const row of acceptedRows) {
      const eventQ = await supabase
        .from('clinic_provisioning_events')
        .select('contract_id')
        .eq('clinic_id', row.clinic_id)
        .eq('step', 'provisioning_complete')
        .eq('status', 'success')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (eventQ.data?.contract_id) {
        const contractQ = await supabase
          .from('contracts')
          .select('proposal_id')
          .eq('id', eventQ.data.contract_id as string)
          .maybeSingle();
        if (contractQ.data?.proposal_id) {
          const proposalQ = await supabase
            .from('proposals')
            .select('lead_id')
            .eq('id', contractQ.data.proposal_id as string)
            .maybeSingle();
          const leadId = proposalQ.data?.lead_id as string | undefined;
          if (leadId) {
            // Transita stage = 'provisioned' (idempotente).
            await supabase
              .from('leads')
              .update({ stage: 'provisioned' })
              .eq('id', leadId)
              .neq('stage', 'provisioned');

            // Activity de aceite — timeline da Karla saber que
            // o owner clicou no link e ativou.
            await supabase.from('lead_activities').insert({
              lead_id: leadId,
              activity_type: 'invite_accepted',
              description: 'Owner aceitou o convite e ativou o acesso à plataforma.',
              metadata: {
                clinic_id: row.clinic_id,
                user_id: userId,
                accepted_at: new Date().toISOString(),
              },
            });
          }
        }
      }
    }

    // 3. Refresh sessão pra trazer JWT atualizado com claims novas
    //    (clinic_ids, current_clinic_id, role_key,
    //    current_clinic_subscription_status). Importante: o claim
    //    `current_clinic_subscription_status` é injetado pelo
    //    `custom_access_token_hook` (migration 0063) e é o que decide
    //    o roteamento abaixo.
    const refreshRes = await supabase.auth.refreshSession();
    if (refreshRes.error) {
      // Não-fatal — temos a sessão antiga. Loga e segue pra routing.
      console.warn('[welcome] refreshSession failed:', refreshRes.error.message);
    }

    // 4. Decide destino e navega.
    const newAccessToken = refreshRes.data.session?.access_token;
    setPhase('routing');
    routeToDestination(newAccessToken);
  }

  /**
   * Fase 4: Decide destino com base no claim `current_clinic_subscription_status`
   * do JWT atualizado. Espelha o `RequireClinicSubscriptionActive` —
   * mesma lógica de gate (ADR 0009 Modelo C híbrido) mas aplicada como
   * navigation explícita (UX: owner vai direto pro destino certo, sem
   * passar por /dashboard pra ser redirecionado).
   */
  function routeToDestination(accessToken: string | null | undefined) {
    const claims = decodeJwtClaims(accessToken);
    const status = claims?.current_clinic_subscription_status ?? null;

    if (status === 'incomplete') {
      navigate('/clinic/payment-pending', { replace: true });
      return;
    }
    if (status === 'canceled' || status === 'paused') {
      navigate('/clinic/suspended', { replace: true });
      return;
    }
    // 'trialing' / 'active' / 'past_due' / null → libera /dashboard
    navigate('/dashboard', { replace: true });
  }

  // ─── Renderização dirigida pelo phase ──────────────────────────────────
  // Estados intermediários (loading, establishing, accepting, routing)
  // mostram spinner — owner não interage. Apenas `password-required` e
  // `error` mostram UI interativa.

  if (
    loading ||
    phase === 'pending' ||
    phase === 'establishing-session' ||
    phase === 'accepting' ||
    phase === 'routing'
  ) {
    return <FullPageSpinner />;
  }

  const fullName = (session?.user?.user_metadata?.['full_name'] as string | undefined) ?? '';
  const firstName = fullName.split(' ')[0] || '';
  const email = session?.user?.email ?? '';

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow="🎉"
          title={firstName ? t('welcome.titleWithName', { name: firstName }) : t('welcome.title')}
          description={
            phase === 'password-required'
              ? t('welcome.description')
              : t('welcome.errors.description')
          }
        />

        {phase === 'password-required' && session?.user ? (
          <PasswordSetForm
            email={email}
            onPasswordSaved={async () => {
              // Após salvar senha, refresh local da sessão pra obter
              // user_metadata atualizado, e segue pra fase de aceite.
              await supabase.auth.refreshSession();
              const userId = session.user.id;
              await runAcceptance(userId);
            }}
            busy={false}
          />
        ) : null}

        {phase === 'error' ? (
          <PageSectionCard
            title={t('welcome.errors.title')}
            description={t('welcome.errors.description')}
          >
            <Button
              type="button"
              size="lg"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              {t('welcome.errors.retry')}
            </Button>
          </PageSectionCard>
        ) : null}
      </PageContainer>
    </ClinicShell>
  );
}
